import React, { useState, useEffect } from 'react';
import { Grid, List, Button, Typography, ButtonGroup } from '@mui/material';
import { Loader, Button as SMButton } from '../../../../../components';
import { Close, Save } from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { useParams, useHistory } from 'react-router';
import {
  getNavigationHierarchy,
  getNavigationIdsForRole,
  updateNavigationAuthorization
} from '../../../../../fetch';
import {
  INavigationHierarchy,
  INavigationRoleIds,
  INavigationAuthorization
} from '../../../../../models';
import { NavigationItem } from './navigation-item';

// Recursively gather all IDs for a given navigation node.
const getAllNavigationIds = (node: INavigationHierarchy): number[] => {
  let ids = [node.navigationId];
  if (node.childNavigations && node.childNavigations.length > 0) {
    node.childNavigations.forEach(child => {
      ids = ids.concat(getAllNavigationIds(child));
    });
  }
  return ids;
};

export const NavigationAuthorization: React.FC = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { roleId } = useParams<{ roleId: string }>();
  const [isSaving, setIsSaving] = useState(false);

  const { data: hierarchy, isLoading: loadingHierarchy } = useQuery<INavigationHierarchy[], Error>(
    'navigationHierarchy',
    getNavigationHierarchy
  );

  const { data: assignedData, isLoading: loadingAssigned } = useQuery<INavigationRoleIds[], Error>(
    ['navigationIdsForRole', roleId],
    () => getNavigationIdsForRole(Number(roleId)),
    { enabled: !!roleId }
  );

  const [assignedIds, setAssignedIds] = useState<number[]>([]);
  const [originalAssignedIds, setOriginalAssignedIds] = useState<number[]>([]);

  useEffect(() => {
    if (assignedData) {
      const ids = assignedData.map(item => item.navigationId);
      setAssignedIds(ids);
      setOriginalAssignedIds(ids);
    }
  }, [assignedData]);

  // Updated onToggle: if assigning a node, also add all its ancestors (parentChain).
  const onToggle = (
    node: INavigationHierarchy,
    newAssigned: boolean,
    parentChain: INavigationHierarchy[] = []
  ) => {
    const subtreeIds = getAllNavigationIds(node);
    if (newAssigned) {
      // If assigning, add the node's subtree and also every parent in the chain.
      const parentIds = parentChain.map(parent => parent.navigationId);
      const newIds = Array.from(new Set([...assignedIds, ...subtreeIds, ...parentIds]));
      setAssignedIds(newIds);
    } else {
      // If unassigning, remove the node's subtree.
      const newIds = assignedIds.filter(id => !subtreeIds.includes(id));
      setAssignedIds(newIds);
    }
  };

  const handleSave = async () => {
    const addedNavigationIds = assignedIds.filter(id => !originalAssignedIds.includes(id));
    const removedNavigationIds = originalAssignedIds.filter(id => !assignedIds.includes(id));

    setIsSaving(true);
    try {
      await updateNavigationAuthorization(roleId, {
        addedNavigationIds,
        removedNavigationIds
      } as INavigationAuthorization);
      enqueueSnackbar('Navigation permissions updated successfully!', { variant: 'success' });
      setOriginalAssignedIds(assignedIds);
      queryClient.invalidateQueries(['getAvailableNavigation']);
    } catch (error) {
      enqueueSnackbar('Error updating navigation authorization', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (loadingHierarchy || loadingAssigned) {
    return <Loader type="inline" position="centered" />;
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h6">Select Navigation</Typography>
      </Grid>
      <Grid item xs={12}>
        <List
          component="div"
          aria-labelledby="nested-list-subheader"
          sx={{
            maxHeight: '26rem',
            overflow: 'auto'
          }}
        >
          {hierarchy?.map(item => (
            <NavigationItem
              key={item.navigationId}
              node={item}
              assignedIds={assignedIds}
              onToggle={onToggle}
            />
          ))}
        </List>
      </Grid>
      <Grid
        container
        alignItems="center"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: theme => theme.spacing(1)
        }}
      >
        <ButtonGroup>
          <SMButton
            id="save"
            color="primary"
            variant="contained"
            disabled={isSaving}
            startIcon={<Save />}
            onClick={handleSave}
          >
            Save Navigation
          </SMButton>
          <Button
            id="remove"
            variant="contained"
            color="inherit"
            startIcon={<Close />}
            onClick={() => {
              history.push('/admin/user-tools/unified-user-management/manage-roles-and-titles');
            }}
          >
            Cancel
          </Button>
        </ButtonGroup>
      </Grid>
    </Grid>
  );
};
